import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Star, Truck, Clock, CheckCircle2, TicketPercent } from "lucide-react";
import { Seo } from "@/components/Seo";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { PHONE, PHONE_TEL } from "@/lib/site";

export default function Welcome() {
  const [form, setForm] = useState({ name: "", phone: "", email: "" });
  const [done, setDone] = useState(false);
  const [sending, setSending] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await api.post("/voucher/claim", {
        name: form.name,
        phone: form.phone,
        email: form.email || null,
      });
      setDone(true);
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Something went wrong — please call 480-940-6023.");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="grain min-h-screen bg-brand-navy text-white">
      <Seo
        title="$10 Off Your First Pickup | Oxford Cleaners Chandler AZ"
        description="New customer? Claim $10 off your first dry cleaning pickup & delivery order from Oxford Cleaners — free pickup across Chandler, Gilbert, Maricopa and the East Valley."
        path="/welcome"
        noindex
      />
      <div className="mx-auto max-w-xl px-4 py-14 sm:py-20">
        <motion.div
          data-testid="welcome-page"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
          className="text-center"
        >
          <div className="flex items-center justify-center gap-2.5">
            <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-fall-amber">
              <Star className="h-6 w-6 fill-brand-navy text-brand-navy" />
            </span>
            <span className="font-display text-xl font-extrabold tracking-tight">Oxford Cleaners</span>
          </div>
          <p className="mt-1.5 text-[10px] font-bold uppercase tracking-[0.3em] text-amber-300">
            Veteran &amp; family-owned · Chandler, AZ
          </p>

          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ delay: 0.2, type: "spring", stiffness: 200, damping: 16 }}
            className="mx-auto mt-8 rounded-[2rem] border-2 border-fall-amber/60 bg-gradient-to-b from-fall-amber/15 to-transparent p-7"
          >
            <TicketPercent className="mx-auto h-10 w-10 text-fall-amber" />
            <h1 data-testid="welcome-heading" className="mt-3 font-display text-3xl font-extrabold tracking-tight sm:text-4xl">
              $10 off your first pickup
            </h1>
            <p className="mt-2 text-sm font-semibold text-slate-300">
              New-customer welcome gift — on top of free pickup &amp; delivery
            </p>
          </motion.div>

          <div className="mx-auto mt-8 max-w-sm space-y-3 text-left">
            {[
              { icon: Truck, text: "We pick up from your doorstep — free" },
              { icon: Clock, text: "Fresh and back home in about 48 hours" },
              { icon: Star, text: "4.7★ from 90+ Google reviews" },
            ].map((f, i) => (
              <motion.p
                key={i}
                initial={{ opacity: 0, x: -14 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.4 + i * 0.15 }}
                className="flex items-center gap-3 rounded-2xl bg-white/5 px-4 py-3 text-sm font-semibold"
              >
                <f.icon className="h-5 w-5 shrink-0 text-fall-amber" /> {f.text}
              </motion.p>
            ))}
          </div>

          {done ? (
            <motion.div
              data-testid="welcome-success"
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              className="mt-8 rounded-3xl bg-white p-7 text-center"
            >
              <CheckCircle2 className="mx-auto h-12 w-12 text-emerald-500" />
              <p className="mt-3 font-display text-xl font-extrabold text-brand-navy">Voucher claimed!</p>
              <p className="mt-1.5 text-sm text-slate-600">
                We&apos;ll text or call you shortly to schedule your free pickup. Just mention your $10
                voucher when we drop everything off.
              </p>
              <a href={PHONE_TEL} className="mt-4 inline-block rounded-full bg-brand-navy px-6 py-3 text-xs font-extrabold uppercase tracking-widest text-white">
                Call {PHONE}
              </a>
            </motion.div>
          ) : (
            <form data-testid="welcome-form" onSubmit={submit} className="mx-auto mt-8 max-w-sm space-y-2.5">
              <input
                data-testid="welcome-name-input"
                required
                minLength={2}
                placeholder="Your name"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                className="w-full rounded-xl border border-white/15 bg-white/10 px-4 py-3.5 text-sm font-semibold text-white placeholder:text-slate-500 focus:border-fall-amber focus:outline-none"
              />
              <input
                data-testid="welcome-phone-input"
                required
                minLength={7}
                type="tel"
                placeholder="Phone number"
                value={form.phone}
                onChange={(e) => setForm({ ...form, phone: e.target.value })}
                className="w-full rounded-xl border border-white/15 bg-white/10 px-4 py-3.5 text-sm font-semibold text-white placeholder:text-slate-500 focus:border-fall-amber focus:outline-none"
              />
              <input
                data-testid="welcome-email-input"
                type="email"
                placeholder="Email (optional)"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                className="w-full rounded-xl border border-white/15 bg-white/10 px-4 py-3.5 text-sm font-semibold text-white placeholder:text-slate-500 focus:border-fall-amber focus:outline-none"
              />
              <button
                data-testid="welcome-claim-button"
                type="submit"
                disabled={sending}
                className="w-full rounded-xl bg-fall-amber px-6 py-4 font-display text-sm font-extrabold uppercase tracking-widest text-brand-navy shadow-lg shadow-fall-amber/25 transition-transform hover:scale-[1.02] disabled:opacity-60"
              >
                {sending ? "Claiming…" : "Claim my $10 voucher"}
              </button>
              <p className="text-[11px] font-semibold text-slate-400">One per new customer. No card needed to claim.</p>
            </form>
          )}

          <p className="mt-10 text-xs text-slate-500">
            5865 W Ray Rd, Suite 3, Chandler, AZ ·{" "}
            <a href={PHONE_TEL} className="font-bold text-amber-300">{PHONE}</a>
          </p>
          <Link data-testid="welcome-home-link" to="/" className="mt-2 inline-block text-xs font-semibold text-slate-400 underline-offset-2 hover:text-fall-amber hover:underline">
            Visit our full website →
          </Link>
        </motion.div>
      </div>
    </div>
  );
}
